import React, { RefObject, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { HELP_SECTIONS } from './sections';
import { highlightSegments, SEARCH_SUGGESTIONS } from './helpSearch';
import type { HelpSearchResult } from './useHelpSearch';

const Highlighted: React.FC<{ text: string; terms: string[][] }> = ({ text, terms }) => (
  <>
    {highlightSegments(text, terms).map((seg, i) =>
      seg.hit ? (
        <mark key={i} className="bg-yellow-200 text-slate-900 rounded-[2px]">
          {seg.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{seg.text}</React.Fragment>
      )
    )}
  </>
);

const BoxButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className="shrink-0 p-1.5 md:p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors"
  >
    {children}
  </button>
);

interface HelpSearchBoxProps {
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  /** Enter(false) / Shift+Enter(true) */
  onEnter: (backward: boolean) => void;
  onFocus?: () => void;
  onClear: () => void;
  onPrev: () => void;
  onNext: () => void;
  current: number;
  total: number;
  truncated: boolean;
  pending: boolean;
}

/** 검색창 — 데스크톱 목차 패널 위와 모바일 목차 막대 두 자리에서 같은 상태로 쓴다 */
export const HelpSearchBox: React.FC<HelpSearchBoxProps> = ({
  inputRef,
  value,
  onChange,
  onEnter,
  onFocus,
  onClear,
  onPrev,
  onNext,
  current,
  total,
  truncated,
  pending,
}) => {
  const hasQuery = value.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    // 한글 마지막 음절을 확정하는 Enter는 이동으로 치지 않는다
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    onEnter(e.shiftKey);
  };

  return (
    <div className="flex items-center gap-0.5 h-10 pl-2.5 pr-1 bg-white border border-slate-200 rounded-lg transition-colors focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-100">
      <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      {/* type="search"는 쓰지 않는다 — 브라우저 기본 × 버튼이 아래 지우기 버튼과 겹친다 */}
      <input
        ref={inputRef}
        type="text"
        role="searchbox"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        spellCheck={false}
        aria-label="설명서 검색"
        placeholder="기능이나 낱말로 찾기"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        className="flex-1 min-w-0 ml-1.5 bg-transparent text-sm text-slate-800 outline-none"
      />
      {hasQuery && (
        <>
          <span className="shrink-0 px-1 text-[11px] text-slate-500" aria-live="polite">
            {pending ? '' : total ? `${current + 1}/${total}${truncated ? '+' : ''}` : '0/0'}
          </span>
          <BoxButton label="이전 결과 (Shift+Enter)" onClick={onPrev} disabled={!total}>
            <ChevronUp className="h-4 w-4" />
          </BoxButton>
          <BoxButton label="다음 결과 (Enter)" onClick={onNext} disabled={!total}>
            <ChevronDown className="h-4 w-4" />
          </BoxButton>
        </>
      )}
      {value && (
        <BoxButton label="검색어 지우기 (Esc)" onClick={onClear}>
          <X className="h-4 w-4" />
        </BoxButton>
      )}
    </div>
  );
};

interface HelpSearchResultsProps {
  terms: string[][];
  results: HelpSearchResult[];
  truncated: boolean;
  pending: boolean;
  /** 현재 매치가 속한 결과 — 강조 + 목록 안 자동 스크롤 */
  activeIndex: number;
  onSelect: (resultIndex: number) => void;
  onSuggest: (query: string) => void;
}

interface ResultGroup {
  sectionId: string;
  chapter: number | null;
  items: number[];
}

/** 결과 목록 — 문서 순서 그대로 장별로 묶는다(책 색인처럼). 장 머리글 매치는 장 이름 줄 자체가 결과가 된다 */
export const HelpSearchResults: React.FC<HelpSearchResultsProps> = ({
  terms,
  results,
  truncated,
  pending,
  activeIndex,
  onSelect,
  onSuggest,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-result-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!results.length) {
    if (pending) return null;
    return (
      <div className="px-2 py-8 text-center">
        <p className="text-sm font-medium text-slate-700">찾는 내용이 없습니다</p>
        <p className="mt-1 text-xs text-slate-500">다른 낱말로 찾아 보세요</p>
        <div className="mt-4 flex flex-wrap justify-center gap-1.5">
          {SEARCH_SUGGESTIONS.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => onSuggest(word)}
              className="px-2.5 py-1 min-h-[32px] text-xs text-slate-600 bg-white border border-slate-200 rounded-full hover:border-brand-600 hover:text-brand-700 transition-colors"
            >
              {word}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const groups: ResultGroup[] = [];
  results.forEach((r, i) => {
    let g = groups[groups.length - 1];
    if (!g || g.sectionId !== r.sectionId) {
      g = { sectionId: r.sectionId, chapter: null, items: [] };
      groups.push(g);
    }
    if (r.isChapter) g.chapter = i;
    else g.items.push(i);
  });

  const itemClass = (active: boolean) =>
    `w-full text-left rounded-lg transition-colors ${active ? 'bg-brand-100' : 'hover:bg-slate-100'}`;

  return (
    <div ref={listRef}>
      <p className="px-3 pb-2 text-xs text-slate-500">
        검색 결과 {results.length}곳{truncated ? ' 이상' : ''}
      </p>
      <div className="space-y-3">
        {groups.map((g) => {
          const section = HELP_SECTIONS.find((s) => s.id === g.sectionId);
          if (!section) return null;
          const Icon = section.icon;
          const chapterLabel = (
            <span className="flex items-center gap-2 min-w-0">
              <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate">
                {g.chapter !== null ? <Highlighted text={section.title} terms={terms} /> : section.title}
              </span>
            </span>
          );
          return (
            <div key={g.sectionId}>
              {g.chapter !== null ? (
                <button
                  type="button"
                  data-result-index={g.chapter}
                  aria-current={activeIndex === g.chapter ? 'true' : undefined}
                  onClick={() => onSelect(g.chapter as number)}
                  className={`${itemClass(activeIndex === g.chapter)} px-3 py-1.5 text-xs font-semibold text-slate-600`}
                >
                  {chapterLabel}
                </button>
              ) : (
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-600">{chapterLabel}</div>
              )}
              {g.items.map((i) => {
                const r = results[i];
                const active = activeIndex === i;
                return (
                  <button
                    key={r.key}
                    type="button"
                    data-result-index={i}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => onSelect(i)}
                    className={`${itemClass(active)} px-3 py-2`}
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800">
                        <Highlighted text={r.heading} terms={terms} />
                      </span>
                      {r.hitCount > 1 && (
                        <span className="shrink-0 text-[11px] text-slate-400">{r.hitCount}</span>
                      )}
                    </span>
                    {r.snippet && (
                      <span className="mt-0.5 text-xs text-slate-500 leading-relaxed line-clamp-2 break-anywhere">
                        <Highlighted text={r.snippet} terms={terms} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
